/**
 * Copyright © Veeam Software Group GmbH.
 */

import React from 'react';
import { capitalize, DurationFormatTypes } from '@veeam-vspc/shared/helpers';
import {
    Dialog,
    DIALOG_SIZE,
    DurationColumn,
    FieldsetBlocks,
    PortalSpinner,
    SizeColumn,
    useGlobalAddons,
    useGlobalLang,
} from '@veeam-vspc/shared/components';

import type { BackupJobStatisticsModel, BackupServerJobGetJobStatisticsParam } from '@veeam-vspc/models/web-controllers';
import type { FieldsetBlockProps } from '@veeam-vspc/shared/components';
import type { VspcLang } from 'configs/languages';

import { useTransportRequest } from 'views/hooks';
import { JobStatisticsLabelText as LabelText } from '../JobStatisticsLabelText/JobStatisticsLabelText';
import { JobStatisticsValueText as ValueText } from '../JobStatisticsValueText/JobStatisticsValueText';

export interface JobStatisticsDialogProps {
    deactivate: () => void;
    jobUid: string;
    title: string;
}

export const JobStatisticsDialog: React.FC<JobStatisticsDialogProps> = ({ deactivate, jobUid, title }) => {
    const lang = useGlobalLang<VspcLang>();
    const {
        loading,
        data,
    } = useTransportRequest<BackupServerJobGetJobStatisticsParam, BackupJobStatisticsModel>('/BackupServerJob/GetJobStatistics', { jobUid });
    const { portalEnums } = useGlobalAddons();
    const { durationSec, avgDurationSec, processingRate, bottleneck, transferredData } = (data as BackupJobStatisticsModel) || {};

    const fieldsetBlocksData: FieldsetBlockProps[] = data
        ? [
            {
                sections: [
                    {
                        title: <LabelText text={lang.SUMMARY} isBold />,
                        items: [
                            {
                                label: <LabelText text={lang.DURATION} />,
                                value: (
                                    <ValueText
                                        text={(
                                            <DurationColumn
                                                value={durationSec}
                                                formatType={DurationFormatTypes.TIMEw8}
                                            />
                                        )}
                                    />
                                ),
                            },
                            {
                                label: <LabelText text={capitalize(lang.AVG_DURATION, true)} />,
                                value: (
                                    <ValueText
                                        text={(
                                            <DurationColumn
                                                value={avgDurationSec}
                                                formatType={DurationFormatTypes.TIMEw8}
                                            />
                                        )}
                                    />
                                ),
                            },
                            {
                                label: <LabelText text={capitalize(lang.PROCESSING_RATE, true)} />,
                                value: (
                                    <ValueText
                                        text={(
                                            <SizeColumn
                                                value={processingRate}
                                                unitName='B/s'
                                                pointFixed={1}
                                            />
                                        )}
                                    />
                                ),
                            },
                            {
                                label: <LabelText text={lang.BOTTLENECK} />,
                                value: (
                                    <ValueText
                                        text={bottleneck ? portalEnums.getEnumDescription('cdpBottleneckRepresentation', bottleneck) : null}
                                    />
                                ),
                            },
                        ],
                    },
                    {
                        title: <LabelText text={lang.DATA} isBold />,
                        items: [
                            {
                                label: <LabelText text={lang.READ} />,
                                value: <ValueText text={<SizeColumn value={transferredData?.readDataKb} pointFixed={1} />} />,
                            },
                            {
                                label: <LabelText text={lang.TRANSFERRED} />,
                                value: <ValueText text={<SizeColumn value={transferredData?.transferredDataKb} pointFixed={1} />} />,
                            },
                            {
                                label: <LabelText text={lang.TOTAL} />,
                                value: <ValueText text={<SizeColumn value={transferredData?.processedDataKb} pointFixed={1} />} />,
                            },
                        ],
                    },
                ],
            },
        ]
        : [];

    return (
        <Dialog
            header={title}
            size={{
                width: DIALOG_SIZE.xs,
                height: DIALOG_SIZE.xs,
            }}
            onRequestClose={deactivate}
            actions={[
                { text: lang.CLOSE, onClick: deactivate },
            ]}
        >
            {loading && <PortalSpinner whiteBg />}
            {!loading && <FieldsetBlocks items={fieldsetBlocksData} />}
        </Dialog>
    );
};
